import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import needHelpSupportIcon from '../../assets/images/need-help-support-icon.png';
import PricingPlansCarousel from '../../components/pricing/PricingPlansCarousel';
import FoundationPackPopup from '../../components/pricing/FoundationPackPopup';
import PaymentStatusModal from '../../components/pricing/PaymentStatusModal';
import SubscriptionDetailsModal from '../../components/profile/SubscriptionDetailsModal';
import { ROUTES } from '../../constants/routes';
import { useSubscriptionContext } from '../../context/SubscriptionContext';
import { useHomeDashboard } from '../../hooks/useHomeDashboard';
import { useFoundationPackCheckout, usePlanPayment } from '../../hooks/usePlanPayment';
import { mapSubscriptionDetails, mapUserSubscriptions } from '../../utils/subscriptionMappers';

function MyPlanCard({ plan, onViewDetails }) {
  return (
    <article
      className={`rounded-[20px] px-4 py-4 sm:px-5 sm:py-5 ${
        plan.active ? 'bg-[#C8D9FF]' : 'bg-[#F3F2F3]'
      }`}
    >
      <div className="grid grid-cols-[1fr_auto] grid-rows-[auto_auto_auto] items-start gap-x-8 gap-y-3">
        <h3 className="text-base font-medium leading-snug text-[#1B1B1B] sm:text-[20px]">
          {plan.title}
        </h3>
        <span
          className={`justify-self-end rounded-full px-4 py-1.5 text-sm font-semibold text-white ${
            plan.active ? 'bg-[#69991D]' : 'bg-[#40330F]'
          }`}
        >
          {plan.active ? 'Active' : 'Expired'}
        </span>

        <div className="col-start-1 row-start-2 h-2 w-full overflow-hidden rounded-full bg-[#555555]">
          {plan.active ? (
            <div
              className="h-full rounded-full bg-[#76A621] transition-all"
              style={{ width: `${Math.round(plan.remainingProgress * 100)}%` }}
            />
          ) : (
            <div className="h-full w-full rounded-full bg-[#BDBDBD]" />
          )}
        </div>

        <div className="col-start-1 row-start-3 flex items-center justify-between gap-4 text-sm text-[#999999]">
          <span>{plan.validLabel}</span>
          <span className="shrink-0">{plan.daysLabel}</span>
        </div>

        <button
          type="button"
          onClick={onViewDetails}
          className="col-start-2 row-start-3 justify-self-end text-sm font-semibold text-[#9369CC] transition hover:underline sm:text-[14px]"
        >
          View Details
        </button>
      </div>
    </article>
  );
}

function PlanCardSkeleton() {
  return <div className="h-[148px] animate-pulse rounded-[20px] bg-white/60" />;
}

export default function ProfilePlanPage() {
  const navigate = useNavigate();
  const { data: dashboardData, loading: plansLoading } = useHomeDashboard();
  const {
    subscriptions,
    loading: subscriptionsLoading,
    isPlanSubscribed,
    refetch,
  } = useSubscriptionContext();

  const {
    startPayment,
    payingPlanId,
    paymentError,
    paymentSuccess,
    clearPaymentFeedback,
    paymentStatus,
    paymentMeta,
    statusMessage,
    checkingStatus,
    closePaymentStatus,
    openPaymentWindow,
    checkPaymentStatus,
  } = usePlanPayment({ onSuccess: refetch });

  const [toast, setToast] = useState(null);
  const [detailsSubscriptionId, setDetailsSubscriptionId] = useState(null);

  const myPlans = useMemo(() => mapUserSubscriptions(subscriptions), [subscriptions]);

  const selectedPlanDetails = useMemo(() => {
    if (!detailsSubscriptionId) return null;
    const subscription = subscriptions.find((item) => String(item.id) === String(detailsSubscriptionId));
    return subscription ? mapSubscriptionDetails(subscription) : null;
  }, [detailsSubscriptionId, subscriptions]);

  const tabs = useMemo(() => {
    if (!dashboardData?.plansByCategory) return [];
    return Object.values(dashboardData.plansByCategory).map((group) => ({
      id: group.id,
      label: group.label,
    }));
  }, [dashboardData?.plansByCategory]);

  const [activeClass, setActiveClass] = useState('');

  useEffect(() => {
    if (tabs.length && !tabs.find((tab) => tab.id === activeClass)) {
      setActiveClass(tabs[0].id);
    }
  }, [tabs, activeClass]);

  const plans = useMemo(() => {
    return dashboardData?.plansByCategory?.[activeClass]?.plans || [];
  }, [dashboardData?.plansByCategory, activeClass]);

  const {
    open: packOpen,
    selectedPlanId,
    setSelectedPlanId,
    openPack,
    closePack,
    handleProceed,
  } = useFoundationPackCheckout({
    plans,
    startPayment,
    isPlanSubscribed,
  });

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(null), 3000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (paymentStatus) return;
    if (paymentSuccess) {
      setToast({ message: paymentSuccess, type: 'success' });
      clearPaymentFeedback();
    } else if (paymentError) {
      setToast({ message: paymentError, type: 'error' });
      clearPaymentFeedback();
    }
  }, [paymentSuccess, paymentError, paymentStatus, clearPaymentFeedback]);

  const handleViewDetails = (planId) => {
    setDetailsSubscriptionId(planId);
  };

  const handleDownloadInvoice = (plan) => {
    if (plan?.invoiceUrl) {
      window.open(plan.invoiceUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    setToast({ message: 'Invoice is not available for this plan yet.', type: 'error' });
  };

  return (
    <>
      {toast && (
        <div
          role="status"
          className={`fixed left-1/2 top-6 z-[60] -translate-x-1/2 rounded-lg px-4 py-2.5 text-sm font-medium text-white shadow-lg ${toast.type === 'error' ? 'bg-red-600' : 'bg-[#2e7d32]'
            }`}
        >
          {toast.message}
        </div>
      )}

      <main className="mx-auto min-w-0 w-full max-w-[980px] sm:py-10">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-[#9369CC] sm:text-[32px]">Plan &amp; Subscription</h1>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-[#000000]">
            Choose your plan and enjoy uninterrupted access to all features. From expert guidance to
            complete course materials
          </p>
        </div>

        <section className="my-10 rounded-2xl bg-gradient-to-r from-[#C4F2FF] to-[#E9ECFF] p-5 shadow-[0_8px_30px_rgba(0,0,0,0.08)] sm:p-6">
          <h2 className="text-sm font-bold text-[#1B1B1B] sm:text-base">My plans</h2>

          {subscriptionsLoading ? (
            <div className="mt-4 flex flex-col gap-4">
              {[1, 2].map((n) => (
                <PlanCardSkeleton key={n} />
              ))}
            </div>
          ) : myPlans.length > 0 ? (
            <div className="mt-4 flex flex-col gap-4">
              {myPlans.map((plan) => (
                <MyPlanCard
                  key={plan.id}
                  plan={plan}
                  onViewDetails={() => handleViewDetails(plan.id)}
                />
              ))}
            </div>
          ) : (
            <p className="mt-4 rounded-2xl bg-white px-4 py-8 text-center text-sm text-[#666666]">
              You do not have any plans yet. Choose a plan below to get started.
            </p>
          )}
        </section>

        <section
          id="profile-pricing-plans"
          className="relative z-10 min-w-0 overflow-visible bg-white px-2 py-8 shadow-[0_8px_40px_rgba(0,0,0,0.12)] sm:px-4 sm:py-10 lg:px-6"
        >
          {tabs.length > 0 && (
            <div className="flex flex-wrap justify-center gap-2 px-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveClass(tab.id)}
                  className={`rounded-lg px-5 py-2.5 text-sm font-bold tracking-wide transition sm:px-7 sm:text-base ${
                    activeClass === tab.id
                      ? 'bg-[#1A87CC] text-white shadow-md'
                      : 'bg-[#F3F2F3] text-[#999999] shadow-sm'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          <PricingPlansCarousel
            plans={plans}
            loading={plansLoading}
            startPayment={openPack}
            payingPlanId={payingPlanId}
            isPlanSubscribed={isPlanSubscribed}
            className="mt-6"
            emptyMessage="No subscription plans available right now."
          />
        </section>
        <section className="mt-8 rounded-xl bg-[#CAC5FF] px-5 py-5 sm:flex sm:items-center sm:justify-between sm:gap-6 sm:px-8 sm:py-6">
          <div className="flex items-start gap-4 sm:items-center">
            <img
              src={needHelpSupportIcon}
              alt=""
              aria-hidden
              className="h-12 w-12 shrink-0 object-contain"
            />
            <div>
              <h3 className="text-[20px] font-semibold text-[#1B1B1B]">Need Help?</h3>
              <p className="mt-1 text-sm text-[#666666]">
                Having trouble with your access or payment? We&apos;re here to help
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate(ROUTES.PROFILE_HELP)}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#3864D2A1] px-5 py-3 text-sm font-semibold text-white transition hover:opacity-90 sm:mt-0 sm:w-auto"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
              />
            </svg>
            Talk to Support
          </button>
        </section>
      </main>

      <FoundationPackPopup
        open={packOpen}
        plans={plans}
        selectedPlanId={selectedPlanId}
        onSelectPlan={setSelectedPlanId}
        onClose={closePack}
        onProceed={handleProceed}
        paying={Boolean(payingPlanId)}
        isPlanSubscribed={isPlanSubscribed}
      />

      <PaymentStatusModal
        open={Boolean(paymentStatus)}
        status={paymentStatus}
        planName={paymentMeta?.planName}
        amount={paymentMeta?.amount}
        message={statusMessage}
        paymentUrl={paymentMeta?.paymentUrl}
        onClose={closePaymentStatus}
        onOpenPayment={openPaymentWindow}
        onCheckPaid={checkPaymentStatus}
        checking={checkingStatus}
      />

      <SubscriptionDetailsModal
        open={Boolean(selectedPlanDetails)}
        plan={selectedPlanDetails}
        onClose={() => setDetailsSubscriptionId(null)}
        onDownloadInvoice={handleDownloadInvoice}
      />
    </>
  );
}
